import axios from 'axios';
import { getAuth } from 'firebase/auth';

const baseUrl = `${process.env.REACT_APP_BASE_URL}`; // Replace with your actual base URL

const headers = {
  'Content-Type': 'application/json',
};

const axiosInstance = axios.create({
    baseURL: baseUrl,
    timeout: 30000,
  });

export type ApiResponse<T> = {
    data: T | null;
    error: string | null;
    // Set when the server refused the call: its HTTP status, and the code from an {error: {code}} body.
    status?: number;
    code?: string;
  };

export enum RequestType {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export interface Failure {
  // Define your failure structure
}

const _logger = {
  i: (message: string) => console.log(message),
  e: (message: string) => console.error(message),
};

export type CallApiOptions = {
  // Sends the signed-in person's Firebase ID token, for endpoints that check who is calling (user management).
  // Leave it off for the multi-day booking endpoints: with a token they change only the caller's own series.
  signedIn?: boolean;
};

export async function callApi<T>(
    endpoint: string,
    body: Record<string, unknown> | undefined | T[],
    requestType: RequestType,
    { signedIn = false }: CallApiOptions = {}
  ): Promise<ApiResponse<T>> {
    _logger.i(`API Request: ${requestType} ${baseUrl}/${endpoint}`);
    _logger.i(`API Request Body: ${JSON.stringify(body)}`);
  
    try {
      const token = signedIn ? await getAuth().currentUser?.getIdToken() : undefined;
      if (signedIn && !token) {
        return {
          data: null,
          error: 'Please sign in to OnePark again.',
        };
      }
      const response = await axiosInstance({
        url: `${baseUrl}/${endpoint}`,
        method: requestType,
        data: JSON.stringify(body),
        headers: token ? { ...headers, Authorization: `Bearer ${token}` } : headers,
      });
  
      _logger.i(`API Response: ${response.status} ${response.statusText}`);
  
      return {
        data: response.data,
        error: null,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        _logger.e(`API Response: ${error.response?.status} ${error.response?.statusText}`);
        const code = error.response?.data?.error?.code;
        return {
          data: null,
          error: error.response?.statusText ?? 'Unknown error',
          status: error.response?.status,
          code: typeof code === 'string' ? code : undefined,
        };
      } else {
        // Handle non-Axios errors if needed
        _logger.e(`API Error:`);
        return {
          data: null,
          error: 'An unexpected error occurred',
        };
      }
    }
  }